import { Capacitor } from '@capacitor/core';
import { initCapacitorStripe } from './capacitorStripe';
import { initQuickActions, handleExoraShortcut } from './capacitorQuickActions';

/**
 * Initialize Capacitor-only native plugins.
 * Safe to call on web — all calls are guarded by isNativePlatform().
 */
export async function initCapacitorPlugins(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  console.log('[Boot][CapInit] START | platform=' + Capacitor.getPlatform());

  // Splash is hidden by main.tsx after UI paint (both platforms)
  // StatusBar — sync with current theme
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const isLightTheme = document.documentElement.classList.contains('light');
    await StatusBar.setStyle({ style: isLightTheme ? Style.Light : Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#000000' }).catch(() => {});
    console.log('[Boot][CapInit] StatusBar configured');
  } catch (e) {
    console.warn('[Boot][CapInit] StatusBar failed', e);
  }

  // Keyboard plugin — iOS only (Android uses visualViewport in capacitorKeyboard.ts)
  if (Capacitor.getPlatform() === 'ios') {
    try {
      const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
      await Keyboard.setScroll({ isDisabled: true });
      await Keyboard.setAccessoryBarVisible({ isVisible: false });
      console.log('[Boot][CapInit] Keyboard configured (iOS)');
    } catch (e) {
      console.warn('[Boot][CapInit] Keyboard failed', e);
    }
  }

  // Stripe — initialize native payment sheet support
  try {
    initCapacitorStripe();
    console.log('[Boot][CapInit] Stripe init called');
  } catch (e) {
    console.warn('[Boot][CapInit] Stripe init threw', e);
  }

  // Quick Actions — iOS home screen shortcuts
  try {
    initQuickActions();
    console.log('[Boot][CapInit] QuickActions init called');
  } catch (e) {
    console.warn('[Boot][CapInit] QuickActions init threw', e);
  }

  // Listen for EXORA_SHORTCUT events dispatched by iOS AppDelegate
  try {
    window.addEventListener('EXORA_SHORTCUT', ((e: CustomEvent<string>) => {
      const type = e.detail;
      if (!type) return;
      console.log(`[Capacitor] Shortcut triggered: ${type}`);
      handleExoraShortcut(type);
    }) as EventListener);
    console.log('[Boot][CapInit] EXORA_SHORTCUT listener attached');
  } catch (e) {
    console.warn('[Boot][CapInit] EXORA_SHORTCUT listener failed', e);
  }

  console.log('[Boot][CapInit] END');
}
